/**
 * Muscle-group domain types + Bulgarian labels for the workout feature.
 *
 * Colocated with the workout models (like `WorkoutExerciseType`) rather than in the
 * global `enums.ts` / `labels.ts`, since muscle groups are workout-specific and the
 * picker, chips, per-workout muscle split and the My Workouts page all consume them.
 *
 * Values MUST match `Planny.Common.Enums.MuscleGroup` / `MuscleGroupCategory`.
 */

export enum MuscleGroup {
  Chest = 100,
  Back = 200,
  Lats = 210,
  Traps = 220,
  LowerBack = 230,
  Shoulders = 300,
  Biceps = 400,
  Triceps = 410,
  Forearms = 420,
  Abs = 500,
  Glutes = 600,
  Quads = 610,
  Hamstrings = 620,
  Calves = 630,
  Cardio = 900,
  Other = 1000,
}

/** High-level buckets the detailed groups roll up into for the muscle-split chart. */
export enum MuscleGroupCategory {
  Chest = 100,
  Back = 200,
  Shoulders = 300,
  Arms = 400,
  Core = 500,
  Legs = 600,
  Cardio = 900,
  Other = 1000,
}

export const MUSCLE_GROUP_TO_CATEGORY: Record<MuscleGroup, MuscleGroupCategory> = {
  [MuscleGroup.Chest]: MuscleGroupCategory.Chest,
  [MuscleGroup.Back]: MuscleGroupCategory.Back,
  [MuscleGroup.Lats]: MuscleGroupCategory.Back,
  [MuscleGroup.Traps]: MuscleGroupCategory.Back,
  [MuscleGroup.LowerBack]: MuscleGroupCategory.Back,
  [MuscleGroup.Shoulders]: MuscleGroupCategory.Shoulders,
  [MuscleGroup.Biceps]: MuscleGroupCategory.Arms,
  [MuscleGroup.Triceps]: MuscleGroupCategory.Arms,
  [MuscleGroup.Forearms]: MuscleGroupCategory.Arms,
  [MuscleGroup.Abs]: MuscleGroupCategory.Core,
  [MuscleGroup.Glutes]: MuscleGroupCategory.Legs,
  [MuscleGroup.Quads]: MuscleGroupCategory.Legs,
  [MuscleGroup.Hamstrings]: MuscleGroupCategory.Legs,
  [MuscleGroup.Calves]: MuscleGroupCategory.Legs,
  [MuscleGroup.Cardio]: MuscleGroupCategory.Cardio,
  [MuscleGroup.Other]: MuscleGroupCategory.Other,
};

export const MUSCLE_GROUP_LABELS: Record<MuscleGroup, string> = {
  [MuscleGroup.Chest]: "Гърди",
  [MuscleGroup.Back]: "Гръб",
  [MuscleGroup.Lats]: "Широк гръбен мускул",
  [MuscleGroup.Traps]: "Трапец",
  [MuscleGroup.LowerBack]: "Кръст",
  [MuscleGroup.Shoulders]: "Рамене",
  [MuscleGroup.Biceps]: "Бицепс",
  [MuscleGroup.Triceps]: "Трицепс",
  [MuscleGroup.Forearms]: "Предмишници",
  [MuscleGroup.Abs]: "Корем",
  [MuscleGroup.Glutes]: "Седалище",
  [MuscleGroup.Quads]: "Предно бедро",
  [MuscleGroup.Hamstrings]: "Задно бедро",
  [MuscleGroup.Calves]: "Прасци",
  [MuscleGroup.Cardio]: "Кардио",
  [MuscleGroup.Other]: "Други",
};

export const MUSCLE_GROUP_CATEGORY_LABELS: Record<MuscleGroupCategory, string> = {
  [MuscleGroupCategory.Chest]: "Гърди",
  [MuscleGroupCategory.Back]: "Гръб",
  [MuscleGroupCategory.Shoulders]: "Рамене",
  [MuscleGroupCategory.Arms]: "Ръце",
  [MuscleGroupCategory.Core]: "Корем",
  [MuscleGroupCategory.Legs]: "Крака",
  [MuscleGroupCategory.Cardio]: "Кардио",
  [MuscleGroupCategory.Other]: "Други",
};

/** Canonical display order for categories (used as a stable tiebreak in the chart). */
export const MUSCLE_GROUP_CATEGORY_ORDER: MuscleGroupCategory[] = [
  MuscleGroupCategory.Chest,
  MuscleGroupCategory.Back,
  MuscleGroupCategory.Shoulders,
  MuscleGroupCategory.Arms,
  MuscleGroupCategory.Legs,
  MuscleGroupCategory.Core,
  MuscleGroupCategory.Cardio,
  MuscleGroupCategory.Other,
];

/** Accent colour per category for the muscle-split bars. */
export const MUSCLE_GROUP_CATEGORY_COLORS: Record<MuscleGroupCategory, string> = {
  [MuscleGroupCategory.Chest]: "#7c83ff",
  [MuscleGroupCategory.Back]: "#5bd1c4",
  [MuscleGroupCategory.Shoulders]: "#ffb84d",
  [MuscleGroupCategory.Arms]: "#ff7eb6",
  [MuscleGroupCategory.Legs]: "#6fcf97",
  [MuscleGroupCategory.Core]: "#9b8cff",
  [MuscleGroupCategory.Cardio]: "#ff6b6b",
  [MuscleGroupCategory.Other]: "#9aa0b4",
};

export interface IMuscleCategoryGroupOptions {
  category: MuscleGroupCategory;
  label: string;
  groups: MuscleGroup[];
}

/** Groups arranged under their category headers, in picker display order. */
export const MUSCLE_GROUPS_BY_CATEGORY: IMuscleCategoryGroupOptions[] = [
  { category: MuscleGroupCategory.Chest, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Chest], groups: [MuscleGroup.Chest] },
  { category: MuscleGroupCategory.Back, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Back], groups: [MuscleGroup.Back, MuscleGroup.Lats, MuscleGroup.Traps, MuscleGroup.LowerBack] },
  { category: MuscleGroupCategory.Shoulders, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Shoulders], groups: [MuscleGroup.Shoulders] },
  { category: MuscleGroupCategory.Arms, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Arms], groups: [MuscleGroup.Biceps, MuscleGroup.Triceps, MuscleGroup.Forearms] },
  { category: MuscleGroupCategory.Legs, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Legs], groups: [MuscleGroup.Glutes, MuscleGroup.Quads, MuscleGroup.Hamstrings, MuscleGroup.Calves] },
  { category: MuscleGroupCategory.Core, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Core], groups: [MuscleGroup.Abs] },
  { category: MuscleGroupCategory.Cardio, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Cardio], groups: [MuscleGroup.Cardio] },
  { category: MuscleGroupCategory.Other, label: MUSCLE_GROUP_CATEGORY_LABELS[MuscleGroupCategory.Other], groups: [MuscleGroup.Other] },
];

export const muscleGroupLabel = (group: MuscleGroup): string =>
  MUSCLE_GROUP_LABELS[group] ?? "—";

/**
 * Accent colour for a single muscle group, resolved through its high-level
 * category. Keeps the exercise-card muscle tags colour-matched to the bars in the
 * per-workout muscle split.
 */
export const muscleGroupColor = (group: MuscleGroup): string =>
  MUSCLE_GROUP_CATEGORY_COLORS[MUSCLE_GROUP_TO_CATEGORY[group]] ??
  MUSCLE_GROUP_CATEGORY_COLORS[MuscleGroupCategory.Other];

/**
 * Compact label for the exercise-card chip: nothing → "—"; one or two groups →
 * their names; more → a "{n} групи" count so the chip stays short.
 */
export const summarizeMuscleGroups = (groups: MuscleGroup[]): string => {
  if (!groups || groups.length === 0) return "—";
  if (groups.length <= 2) return groups.map(muscleGroupLabel).join(", ");
  return `${groups.length} групи`;
};
